import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type RoundStatus, type RoundSummary } from '../lib/api';

const STATUS_LABEL: Record<RoundStatus, string> = {
  draft: 'Draft', ready: 'Ready to Record', recorded: 'Recorded', archived: 'Archived',
};
const STATUSES = Object.keys(STATUS_LABEL) as RoundStatus[];

/* The rounds list. Alyssa can keep as many rounds queued as she likes, so this has to stay
   readable at 30 rounds, not just at 3 — hence the counts and the readiness signal on every
   row rather than only inside the editor. */

export default function RoundsPage() {
  const navigate = useNavigate();
  const [rounds, setRounds] = useState<RoundSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  /* Archived rounds move out of the way rather than disappearing. Filtered client-side from the
     one listRounds call already being made, so both counts are instant and no second request is
     needed. Archiving is organisational only — the round, its items and its play links all keep
     working, because a link dying because someone tidied up is exactly the shoot-day failure
     this project has been designing against. */
  const [tab, setTab] = useState<'active' | 'archived'>('active');

  const load = useCallback(async () => {
    try {
      setRounds(await api.listRounds());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load rounds.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* Next free "Round N", so clicking Create with an empty box still produces something
     sensibly named rather than "Untitled". The title is editable inline in the editor, so
     naming up front is a convenience, never a requirement. */
  function nextRoundName(existing: RoundSummary[]): string {
    const used = existing
      .map((r) => /^(?:series|round)\s+(\d+)/i.exec(r.title.trim())?.[1])
      .filter(Boolean)
      .map(Number);
    return `Series ${used.length ? Math.max(...used) + 1 : existing.length + 1}`;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    /* Deliberately NOT disabled on an empty box. It used to be, at 40% opacity, and it read as
       a broken button: it looks clickable, you click it, nothing happens, and nothing explains
       why. Falling back to a generated name removes the dead state entirely. */
    const name = title.trim() || nextRoundName(rounds || []);
    setCreating(true);
    try {
      const round = await api.createRound({ title: name });
      setTitle('');
      navigate(`/rounds/${round.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the round.');
    } finally {
      setCreating(false);
    }
  }

  const active = (rounds || []).filter((r) => r.status !== 'archived');
  const archived = (rounds || []).filter((r) => r.status === 'archived');
  const shown = tab === 'active' ? active : archived;

  /* Card edits are optimistic. Both are one-field changes that the server either accepts or
     rejects outright, and reverting on failure is cheaper than making the list flicker on every
     rename. */
  const patchRound = (id: string, patch: Partial<RoundSummary>) =>
    setRounds((rs) => (rs ? rs.map((r) => (r.id === id ? { ...r, ...patch } : r)) : rs));

  async function saveTitle(round: RoundSummary, next: string) {
    const title = next.trim();
    if (!title || title === round.title) return;
    patchRound(round.id, { title });
    try {
      await api.updateRound(round.id, { title });
    } catch (err) {
      patchRound(round.id, { title: round.title });
      setError(err instanceof Error ? err.message : 'Could not rename that series.');
    }
  }

  async function saveStatus(round: RoundSummary, status: RoundStatus) {
    if (status === round.status) return;
    patchRound(round.id, { status });
    try {
      await api.updateRound(round.id, { status });
    } catch (err) {
      patchRound(round.id, { status: round.status });
      setError(err instanceof Error ? err.message : 'Could not change that status.');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Series</h1>
          <p className="page-sub">
            Build a series, each gets its own unique link.
          </p>
        </div>
        <form className="inline-form" onSubmit={handleCreate}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`Name it, or leave blank for "${nextRoundName(rounds || [])}"`}
            aria-label="New series name"
            style={{ minWidth: 300 }}
          />
          <button className="btn primary" type="submit" disabled={creating}>
            {creating ? 'Creating…' : 'Create Series'}
          </button>
        </form>
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 20 }}>{error}</div>}

      {rounds && rounds.length > 0 && (
        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'active'} className={tab === 'active' ? 'on' : ''} onClick={() => setTab('active')}>
            Active <span className="tab-count">{active.length}</span>
          </button>
          <button role="tab" aria-selected={tab === 'archived'} className={tab === 'archived' ? 'on' : ''} onClick={() => setTab('archived')}>
            Archive <span className="tab-count">{archived.length}</span>
          </button>
        </div>
      )}

      {rounds === null && !error && (
        <div className="state-block"><div className="sub">Loading series…</div></div>
      )}

      {rounds?.length === 0 && (
        <div className="state-block">
          <div className="big">No series yet</div>
          <div className="sub">
            Name your first series above. You can add headlines one at a time or paste in a
            spreadsheet, and nothing is published until you create a link for it.
          </div>
        </div>
      )}

      {rounds && rounds.length > 0 && shown.length === 0 && (
        <div className="state-block">
          <div className="big">{tab === 'archived' ? 'Nothing archived' : 'Everything is archived'}</div>
          <div className="sub">
            {tab === 'archived'
              ? 'Series you archive will collect here. They keep working — their play links stay live.'
              : 'Every series is in the Archive. Set one back to Draft to bring it here.'}
          </div>
        </div>
      )}

      {shown.length > 0 && (
        <div className="round-grid" data-testid="series-grid">
          {shown.map((round) => (
            <RoundCard
              key={round.id}
              round={round}
              onRename={(title) => void saveTitle(round, title)}
              onStatus={(status) => void saveStatus(round, status)}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* One series in the list.
 *
 * The card is still "click it to open the editor", but the two things people change most often
 * — the name and the status — are editable here, because opening a series just to flip it to
 * Ready and coming straight back out was the common path and it is three navigations.
 *
 * ⚠️ The card cannot be an <a> any more: a <select> and an <input> inside a link are invalid
 * markup and the browser gives the anchor the clicks. It is a plain div with a STRETCHED LINK
 * absolutely covering it, and the two controls raised above that link. Clicking anywhere else
 * on the card — counts, readiness line, padding — still opens the editor.
 */
function RoundCard({
  round, onRename, onStatus,
}: {
  round: RoundSummary;
  onRename: (title: string) => void;
  onStatus: (status: RoundStatus) => void;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = (value: string) => {
    setEditing(false);
    onRename(value);
  };

  return (
    <div className="round-card">
      {/* Covers the whole card, sits UNDER the two controls. aria-label because the visible
          title is a button now, so the link has no text of its own. */}
      <Link to={`/rounds/${round.id}`} className="card-open" aria-label={`Open ${round.title}`} />

      <div className="round-card-head">
        {editing ? (
          <input
            ref={inputRef}
            className="round-title-edit"
            defaultValue={round.title}
            aria-label="Series name"
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(e.currentTarget.value); }
              // Escape abandons the edit. Blur would otherwise save what was half-typed.
              if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
            }}
          />
        ) : (
          <button type="button" className="round-title-btn" onClick={() => setEditing(true)} title="Rename">
            {round.title}
          </button>
        )}

        <select
          className={`round-status-select s-${round.status}`}
          value={round.status}
          aria-label={`Status of ${round.title}`}
          onChange={(e) => onStatus(e.target.value as RoundStatus)}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      <div className="round-counts">
        <span>{round.counts.total} headline{round.counts.total === 1 ? '' : 's'}</span>
        <span className="c-real">{round.counts.real} real</span>
        <span className="c-fake">{round.counts.fake} fake</span>
        {round.targetSize ? <span className="c-dim">target {round.targetSize}</span> : null}
      </div>
      {round.counts.needsArt > 0 ? (
        /* Named separately from the generic "not ready" count, because it is a different
           person's job: this one is waiting on a designer, not on an editor. */
        <div className="round-warn">
          {round.counts.needsArt} waiting on artwork
          {round.counts.missing > round.counts.needsArt
            ? ` · ${round.counts.missing - round.counts.needsArt} other problem${round.counts.missing - round.counts.needsArt === 1 ? '' : 's'}`
            : ''}
        </div>
      ) : round.counts.missing > 0 ? (
        <div className="round-warn">
          {round.counts.missing} headline{round.counts.missing === 1 ? '' : 's'} not ready to record
        </div>
      ) : round.counts.total > 0 ? (
        <div className="round-ok">Ready to record</div>
      ) : (
        <div className="round-warn">Empty — add some headlines</div>
      )}
    </div>
  );
}
